import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Modal, Button, Stack, Text, Menu } from "@mantine/core";
import {
  IconArrowLeft,
  IconBrandInstagram,
  IconBrandLinkedin,
  IconBriefcase,
  IconCalendarEvent,
  IconCheck,
  IconCircleCheck,
  IconCopy,
  IconDots,
  IconFileText,
  IconMail,
  IconMessage,
  IconMessageCircle,
  IconPencil,
  IconPhone,
  IconSpeakerphone,
  IconUser,
  IconUserCheck,
  IconUserOff,
  IconUsers,
  IconActivity,
} from "@tabler/icons-react";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../context/ToastContext";
import { getApiEndpoint } from "../../config/config";
import PageContent from "../shared/PageContent/PageContent";
import PinGate from "../shared/PinGate/PinGate";
import { clearPinUnlockSession } from "../../utils/pinUnlockSession";
import RepDealTab from "../RepManagement/RepDealTab";
import EditRepModal from "./EditRepModal";
import RepDocumentsSection from "./RepDocumentsSection";
import "./RepProfilePage.css";

const DAY_MS = 24 * 60 * 60 * 1000;

const parseDate = value => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

// Whole calendar days from `date` to today
const daysSince = date => {
  const start = new Date(date);
  start.setHours(0, 0, 0, 0);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.max(0, Math.round((today - start) / DAY_MS));
};

const formatDate = (value, options) =>
  parseDate(value)?.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
    ...options,
  }) ?? "";

// $1.2M / $50K / $850, as iOS shows production
const compactCurrency = value => {
  const n = Number(value) || 0;
  const sign = n < 0 ? "-" : "";
  const abs = Math.abs(n);
  const trim = x => String(Math.round(x * 10) / 10).replace(/\.0$/, "");
  if (abs >= 1e6) return `${sign}$${trim(abs / 1e6)}M`;
  if (abs >= 1e3) return `${sign}$${trim(abs / 1e3)}K`;
  return `${sign}$${Math.round(abs)}`;
};

const personName = p =>
  p ? `${p.first_name || ""} ${p.last_name || ""}`.trim() : "";

const initialsOf = rep =>
  `${rep?.first_name?.[0] || ""}${rep?.last_name?.[0] || ""}`.toUpperCase() ||
  "?";

// "ACTIVE TODAY" … "ACTIVE 3MO AGO", tinted like iOS
const activeChip = iso => {
  const date = parseDate(iso);
  if (!date) return null;
  const d = daysSince(date);
  if (d === 0) return { text: "Active today", tone: "good" };
  if (d === 1) return { text: "Active yesterday", tone: "plain" };
  if (d < 7) return { text: `Active ${d}d ago`, tone: "plain" };
  if (d < 30) return { text: `Active ${Math.floor(d / 7)}w ago`, tone: "warn" };
  return { text: `Active ${Math.floor(d / 30)}mo ago`, tone: "warn" };
};

// /v1/rep/:rep_id/history → activity rows, labelled the way iOS does
const activityLabel = item => {
  const type = item.event_type || "";
  if (type === "Recruit Added" || type === "Rep Added") return "Created";
  if (type === "Contact") {
    return (item.contact_method || "").toLowerCase() === "phone"
      ? "Call"
      : "Contact";
  }
  if (type.startsWith("Manager Meeting")) return "Meeting";
  if (type.startsWith("Team Event")) return "Team Event";
  if (type === "Attendance Check-In") return "Check-In";
  return type;
};

const activityIcon = label => {
  const t = label.toLowerCase();
  if (t === "call" || t.includes("contact")) return IconPhone;
  if (t === "created") return IconUser;
  if (t.includes("meeting") || t.includes("team event")) {
    return IconCalendarEvent;
  }
  if (t.includes("proposal") || t.includes("document") || t === "note") {
    return IconFileText;
  }
  if (t.includes("check-in") || t === "milestone") return IconCircleCheck;
  if (t === "pitch" || t.startsWith("slow")) return IconActivity;
  return IconMessage;
};

const HUES = ["#d0521f", "#3b4da8", "#7550a8", "#28a745", "#b5832a", "#3b85c8"];

function RepProfilePage() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { getVerifiedJWT } = useAuth();
  const { showSuccess, showError } = useToast();

  const [rep, setRep] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [history, setHistory] = useState([]);
  const [records, setRecords] = useState(null);
  const [revenue, setRevenue] = useState(null);
  const [tab, setTab] = useState("overview");
  const [showAllActivity, setShowAllActivity] = useState(false);
  const [editing, setEditing] = useState(false);
  // "drop" | "reactivate" while the confirmation is open
  const [confirm, setConfirm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(null);

  const authedGet = useCallback(
    async path => {
      const jwt = await getVerifiedJWT();
      if (!jwt) throw new Error("Authentication failed");
      const response = await fetch(getApiEndpoint(path), {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt}`,
        },
      });
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.message || `HTTP ${response.status}`);
      }
      return (await response.json()).data;
    },
    [getVerifiedJWT]
  );

  const loadRep = useCallback(async () => {
    try {
      setRep(await authedGet(`/v2/rep/${userId}`));
      setError(null);
    } catch (err) {
      setError(err.message || "Couldn't load this rep");
    } finally {
      setLoading(false);
    }
  }, [authedGet, userId]);

  useEffect(() => {
    setLoading(true);
    setTab("overview");
    loadRep();
  }, [loadRep]);

  // Activity, records and revenue are keyed by rep_id, so they wait for it
  const repId = rep?.rep_id;
  useEffect(() => {
    if (!repId) return undefined;
    let cancelled = false;
    authedGet(`/v1/rep/${repId}/history`)
      .then(items => !cancelled && setHistory(items || []))
      .catch(() => !cancelled && setHistory([]));
    authedGet(`/v1/revenue/records?rep_id=${repId}`)
      .then(data => !cancelled && setRecords(data || null))
      .catch(() => !cancelled && setRecords(null));
    authedGet("/v1/revenue/leaderboard")
      .then(data => {
        if (cancelled) return;
        const row = (data?.rows || []).find(
          r => Number(r.rep_id ?? r.reppi_user_id) === Number(repId)
        );
        setRevenue(row || null);
      })
      .catch(() => !cancelled && setRevenue(null));
    return () => {
      cancelled = true;
    };
  }, [authedGet, repId]);

  const activity = useMemo(
    () =>
      history
        .filter(item => item.source !== "attendance_event")
        .map((item, i) => {
          const label = activityLabel(item);
          return {
            key: item.id ?? i,
            label,
            date: formatDate(item.event_date || item.created_at, {
              year: undefined,
            }),
            notes: item.notes,
            Icon: activityIcon(label),
          };
        }),
    [history]
  );

  // Production shows when there's a revenue row or any record; the iOS label
  // says YTD but the board is fetched with no dates, so it's all-time
  const production = useMemo(() => {
    const record = (r, monthOnly) =>
      r
        ? {
            value: compactCurrency(r.sold_revenue),
            accounts: r.sold_count ?? 0,
            date: formatDate(
              r.period_start,
              monthOnly ? { day: undefined } : undefined
            ),
          }
        : null;
    const recs = {
      day: record(records?.best_day),
      week: record(records?.best_week),
      month: record(records?.best_month, true),
    };
    const hasRecords = Boolean(recs.day || recs.week || recs.month);
    if (revenue) {
      const sold = Number(revenue.sold_count) || 0;
      const total = Number(revenue.total_contract_value) || 0;
      const acv = revenue.avg_contract_value ?? (sold > 0 ? total / sold : 0);
      return {
        revenue: compactCurrency(total),
        accounts: sold,
        acv: compactCurrency(acv),
        records: recs,
      };
    }
    if (hasRecords) {
      return { revenue: "—", accounts: 0, acv: "—", records: recs };
    }
    return null;
  }, [records, revenue]);

  const setDeactivated = async (deactivated, cascade) => {
    setBusy(true);
    try {
      const jwt = await getVerifiedJWT();
      if (!jwt) throw new Error("Authentication failed");
      const response = await fetch(
        getApiEndpoint(`/v2/rep/${rep.user_id}/deactivated`),
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwt}`,
          },
          body: JSON.stringify({ deactivated, drop_cascade: cascade }),
        }
      );
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.message || "Request failed");
      }
      showSuccess(
        deactivated
          ? `${personName(rep)} dropped`
          : `${personName(rep)} reactivated`
      );
      setConfirm(null);
      await loadRep();
    } catch (err) {
      showError(
        deactivated
          ? `Couldn't drop ${personName(rep)}: ${err.message}`
          : `Couldn't reactivate ${personName(rep)}: ${err.message}`
      );
    } finally {
      setBusy(false);
    }
  };

  const copy = async (label, value) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(label);
      setTimeout(() => setCopied(c => (c === label ? null : c)), 1500);
    } catch {
      // Clipboard can be blocked; nothing to do
    }
  };

  const back = () => {
    if (window.history.length > 1) navigate(-1);
    else navigate("/reps?tab=org");
  };

  if (loading && !rep) {
    return (
      <PageContent>
        <div className="rep-profile-state">Loading profile...</div>
      </PageContent>
    );
  }
  if (!rep) {
    return (
      <PageContent>
        <div className="rep-profile-state rep-profile-state-error">
          {error || "Couldn't load this rep"}
          <button type="button" className="rep-profile-link" onClick={back}>
            Go back
          </button>
        </div>
      </PageContent>
    );
  }

  const name = personName(rep) || "Rep";
  const isDeactivated = rep.is_active === false || Boolean(rep.deactivated_at);
  const hasDownline = (rep.rep_count ?? 0) > 0;
  const role = rep.rep_type?.name || rep.role_name || "Rep";
  const chip = activeChip(rep.last_active_time);
  const phoneDigits = (rep.phone_number || "").replace(/[^\d+]/g, "");
  const lastContact = parseDate(rep.last_contact_date);

  const tabs = [
    { id: "overview", label: "Overview" },
    production && { id: "production", label: "Production" },
    { id: "training", label: "Training" },
    { id: "pay", label: "Pay" },
  ].filter(Boolean);

  const contactRows = [
    rep.phone_number && {
      label: "Mobile",
      value: rep.phone_number,
      Icon: IconPhone,
      copy: true,
    },
    rep.email && {
      label: "Email",
      value: rep.email,
      Icon: IconMail,
      copy: true,
    },
    rep.instagram_username && {
      label: "Instagram",
      value: rep.instagram_username,
      Icon: IconBrandInstagram,
      copy: true,
    },
    rep.linkedin_profile && {
      label: "LinkedIn",
      value: rep.linkedin_profile,
      Icon: IconBrandLinkedin,
      copy: true,
    },
    rep.created_at && {
      label: "Tenure",
      value: `Joined ${formatDate(rep.created_at)}`,
      Icon: IconBriefcase,
    },
    rep.managers?.length > 0 && {
      label: "Manager",
      value: rep.managers.map(personName).join(", "),
      Icon: IconUsers,
    },
    rep.recruited_by && {
      label: "Recruited by",
      value: personName(rep.recruited_by),
      Icon: IconUserCheck,
    },
    rep.recruiting_source && {
      label: "Source",
      value: rep.recruiting_source.name,
      Icon: IconSpeakerphone,
    },
  ].filter(Boolean);

  const renderOverview = () => {
    const shownActivity = showAllActivity ? activity : activity.slice(0, 5);
    const stats = [
      {
        label: "Last Contact",
        value: lastContact
          ? daysSince(lastContact) === 0
            ? "Today"
            : `${daysSince(lastContact)}d`
          : "—",
        sub: lastContact ? formatDate(lastContact, { year: undefined }) : null,
      },
      { label: "Last One on One", value: "—" },
      { label: "Meeting Attendance", value: "—" },
    ];
    return (
      <>
        <div className="rep-profile-stat-strip">
          {stats.map(stat => (
            <div key={stat.label} className="rep-profile-stat-cell">
              <span className="rep-profile-stat-label">{stat.label}</span>
              <span className="rep-profile-stat-value">{stat.value}</span>
              {stat.sub && (
                <span className="rep-profile-stat-sub">↘ {stat.sub}</span>
              )}
            </div>
          ))}
        </div>

        <section className="rep-profile-section">
          <div className="rep-profile-section-head">
            <span className="rep-profile-section-title">Contact</span>
            <button
              type="button"
              className="rep-profile-link"
              onClick={() => setEditing(true)}
            >
              <IconPencil size={14} stroke={2.25} />
              Edit
            </button>
          </div>
          {contactRows.length > 0 && (
            <div className="rep-profile-card">
              {contactRows.map(row => (
                <div key={row.label} className="rep-profile-contact-row">
                  <span className="rep-profile-contact-icon">
                    <row.Icon size={18} stroke={1.75} />
                  </span>
                  <span className="rep-profile-contact-text">
                    <span className="rep-profile-contact-label">
                      {row.label}
                    </span>
                    <span className="rep-profile-contact-value">
                      {row.value}
                    </span>
                  </span>
                  {row.copy && (
                    <button
                      type="button"
                      className="rep-profile-icon-button"
                      onClick={() => copy(row.label, row.value)}
                      aria-label={`Copy ${row.label}`}
                    >
                      {copied === row.label ? (
                        <IconCheck size={16} />
                      ) : (
                        <IconCopy size={16} />
                      )}
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </section>

        {activity.length > 0 && (
          <section className="rep-profile-section">
            <div className="rep-profile-section-head">
              <span className="rep-profile-section-title">Recent Activity</span>
              {activity.length > 5 && (
                <button
                  type="button"
                  className="rep-profile-link"
                  onClick={() => setShowAllActivity(all => !all)}
                >
                  {showAllActivity ? "show less" : "view all"}
                </button>
              )}
            </div>
            <div className="rep-profile-activity">
              {shownActivity.map(({ key, label, date, notes, Icon }) => (
                <div key={key} className="rep-profile-activity-row">
                  <span className="rep-profile-activity-icon">
                    <Icon size={20} stroke={1.75} />
                  </span>
                  <span className="rep-profile-activity-text">
                    <span className="rep-profile-activity-head">
                      <span className="rep-profile-activity-type">{label}</span>
                      {date && (
                        <span className="rep-profile-activity-date">
                          {date}
                        </span>
                      )}
                    </span>
                    {notes && (
                      <span className="rep-profile-activity-notes">
                        {notes}
                      </span>
                    )}
                  </span>
                </div>
              ))}
            </div>
          </section>
        )}
      </>
    );
  };

  const renderProduction = () => {
    const { records: recs } = production;
    const recordCard = (label, r) =>
      r && (
        <div className="rep-profile-record">
          <span className="rep-profile-stat-label">{label}</span>
          <span className="rep-profile-record-value">{r.value}</span>
          <span className="rep-profile-record-sub">
            {r.accounts} accounts{r.date ? ` · ${r.date}` : ""}
          </span>
        </div>
      );
    return (
      <>
        <section className="rep-profile-section">
          <div className="rep-profile-section-head">
            <span className="rep-profile-section-title">Personal</span>
          </div>
          <div className="rep-profile-stat-block">
            {[
              { label: "Revenue YTD", value: production.revenue },
              { label: "Accounts", value: production.accounts },
              { label: "ACV", value: production.acv },
            ].map(stat => (
              <div key={stat.label} className="rep-profile-stat-block-cell">
                <span className="rep-profile-stat-label">{stat.label}</span>
                <span className="rep-profile-stat-block-value">
                  {stat.value}
                </span>
              </div>
            ))}
          </div>
        </section>
        <section className="rep-profile-section">
          <div className="rep-profile-section-head">
            <span className="rep-profile-section-title">Records</span>
          </div>
          <div className="rep-profile-records">
            {recordCard("Best Day", recs.day)}
            {recordCard("Best Week", recs.week)}
            {recordCard("Best Month", recs.month)}
          </div>
        </section>
      </>
    );
  };

  return (
    <PageContent>
      <div className="rep-profile">
        <div className="rep-profile-nav">
          <button
            type="button"
            className="rep-profile-icon-button"
            onClick={back}
            aria-label="Back"
          >
            <IconArrowLeft size={20} />
          </button>
          <span className="rep-profile-nav-title">Profile</span>
          <Menu position="bottom-end" withinPortal>
            <Menu.Target>
              <button
                type="button"
                className="rep-profile-icon-button rep-profile-more"
                aria-label="More"
              >
                <IconDots size={18} />
              </button>
            </Menu.Target>
            <Menu.Dropdown>
              {isDeactivated ? (
                <Menu.Item
                  leftSection={<IconUserCheck size={16} />}
                  onClick={() => setConfirm("reactivate")}
                >
                  Reactivate
                </Menu.Item>
              ) : (
                <Menu.Item
                  color="red"
                  leftSection={<IconUserOff size={16} />}
                  onClick={() => setConfirm("drop")}
                >
                  Mark as Dropped
                </Menu.Item>
              )}
            </Menu.Dropdown>
          </Menu>
        </div>

        <div className="rep-profile-hero">
          <span
            className="rep-profile-avatar"
            style={{
              background: HUES[Math.abs(rep.rep_id || 0) % HUES.length],
            }}
          >
            {rep.profile_image_url ? (
              <img src={rep.profile_image_url} alt="" />
            ) : (
              initialsOf(rep)
            )}
          </span>
          <h1 className="rep-profile-name">{name}</h1>
          {isDeactivated ? (
            <span className="rep-profile-chip is-warn">Dropped</span>
          ) : (
            chip && (
              <span className={`rep-profile-chip is-${chip.tone}`}>
                <span className="rep-profile-chip-dot" />
                {chip.text}
              </span>
            )
          )}
          {role !== "Rep" && <span className="rep-profile-role">{role}</span>}

          <div className="rep-profile-actions">
            {[
              {
                label: "Message",
                Icon: IconMessageCircle,
                href: phoneDigits ? `sms:${phoneDigits}` : null,
                primary: true,
              },
              {
                label: "Call",
                Icon: IconPhone,
                href: phoneDigits ? `tel:${phoneDigits}` : null,
              },
              {
                label: "Email",
                Icon: IconMail,
                href: rep.email ? `mailto:${rep.email}` : null,
              },
            ].map(({ label, Icon, href, primary }) => (
              <a
                key={label}
                className={`rep-profile-action${primary ? " is-primary" : ""}`}
                href={href || undefined}
                onClick={event => {
                  if (href) return;
                  event.preventDefault();
                  showError(
                    label === "Email"
                      ? `${name} doesn't have an email saved.`
                      : `${name} doesn't have a phone number saved.`
                  );
                }}
              >
                <span className="rep-profile-action-circle">
                  <Icon size={22} stroke={1.75} />
                </span>
                {label}
              </a>
            ))}
          </div>
        </div>

        <div className="rep-profile-tabs" role="tablist">
          {tabs.map(t => (
            <button
              key={t.id}
              type="button"
              role="tab"
              aria-selected={tab === t.id}
              className={`rep-profile-tab${tab === t.id ? " is-active" : ""}`}
              onClick={() => setTab(t.id)}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div className="rep-profile-body">
          {tab === "overview" && renderOverview()}
          {tab === "production" && production && renderProduction()}
          {tab === "training" && (
            <div className="rep-profile-empty">No training data yet</div>
          )}
          {tab === "pay" && (
            <PinGate
              title="Pay is protected"
              description="Enter your security PIN to view and edit this rep's pay."
            >
              <RepDocumentsSection repId={rep.rep_id} />
              <div className="rep-profile-divider" />
              <RepDealTab
                repId={rep.rep_id}
                repName={name}
                onPinRequired={clearPinUnlockSession}
              />
            </PinGate>
          )}
        </div>
      </div>

      <EditRepModal
        opened={editing}
        rep={rep}
        onClose={() => setEditing(false)}
        onSaved={loadRep}
      />

      <Modal
        opened={Boolean(confirm)}
        onClose={() => setConfirm(null)}
        title={
          confirm === "drop"
            ? `Mark ${name} as dropped?`
            : `Reactivate ${name}?`
        }
        centered
        size="md"
      >
        <Stack gap="sm">
          <Text size="sm" c="dimmed">
            {confirm === "drop"
              ? "Dropped reps are hidden from every reps page company-wide. You can restore them anytime from Dropped Off Reps."
              : "Reps who were moved up to another manager when they were dropped stay where they are."}
          </Text>
          {confirm === "drop" && hasDownline && (
            <>
              <Button
                color="red"
                loading={busy}
                onClick={() => setDeactivated(true, true)}
              >
                Drop & hide their whole downline
              </Button>
              <Button
                color="red"
                variant="light"
                disabled={busy}
                onClick={() => setDeactivated(true, false)}
              >
                Drop only {rep.first_name}, keep their downline
              </Button>
            </>
          )}
          {confirm === "drop" && !hasDownline && (
            <Button
              color="red"
              loading={busy}
              onClick={() => setDeactivated(true, false)}
            >
              Drop {rep.first_name}
            </Button>
          )}
          {confirm === "reactivate" && (
            <>
              <Button
                loading={busy}
                onClick={() => setDeactivated(false, true)}
              >
                Reactivate {rep.first_name} and anyone dropped with them
              </Button>
              <Button
                variant="light"
                disabled={busy}
                onClick={() => setDeactivated(false, false)}
              >
                Reactivate only {rep.first_name}
              </Button>
            </>
          )}
          <Button
            variant="default"
            onClick={() => setConfirm(null)}
            disabled={busy}
          >
            Cancel
          </Button>
        </Stack>
      </Modal>
    </PageContent>
  );
}

export default RepProfilePage;
